import { useQuery } from "@tanstack/react-query"
import { Loader2, History, ArrowRight } from "lucide-react"
import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
    getInstrumentAuditLog,
    type AuditLogEntry,
    FIELD_LABELS,
    STATUS_LABELS,
    resolveValue,
} from "@/Wist-blockchain/collections/instruments/instrumentAuditLog.service"

const INITIAL_VISIBLE = 5

// Agrupa entradas del mismo usuario en el mismo guardado (mismo changedAt al segundo)
function groupEntries(entries: AuditLogEntry[]) {
    const groups: { key: string; userId: string; userName: string; changedAt: string; items: AuditLogEntry[] }[] = []
    for (const entry of entries) {
        const second = entry.changedAt.slice(0, 19)
        const last = groups[groups.length - 1]
        if (last && last.userId === entry.userId && last.changedAt.slice(0, 19) === second) {
            last.items.push(entry)
        } else {
            groups.push({ key: entry.$id, userId: entry.userId, userName: entry.userName, changedAt: entry.changedAt, items: [entry] })
        }
    }
    return groups
}

function formatRelative(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const m = Math.floor(diff / 60000)
    if (m < 1)  return "hace un momento"
    if (m < 60) return `hace ${m} min`
    const h = Math.floor(m / 60)
    if (h < 24) return `hace ${h}h`
    const d = Math.floor(h / 24)
    if (d < 30) return `hace ${d} día${d === 1 ? "" : "s"}`
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })
}

function initials(name: string): string {
    return name.split(" ").slice(0, 2).map(word => word[0]?.toUpperCase() ?? "").join("")
}

const STATUS_BADGE: Record<string, string> = {
    operativo:               "bg-emerald-100 text-emerald-700 border-emerald-200",
    operativo_observaciones: "bg-amber-100 text-amber-700 border-amber-200",
    fuera_servicio:          "bg-red-100 text-red-600 border-red-200",
}

function ValueChip({ field, raw }: { field: string; raw: string }) {
    const label = resolveValue(field, raw)
    if (field === "status") {
        const cls = STATUS_BADGE[raw] ?? "bg-gray-100 text-gray-600 border-gray-200"
        return (
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold shrink-0 ${cls}`}>
                {STATUS_LABELS[raw] ?? raw}
            </span>
        )
    }
    if (!raw) return <span className="text-muted-foreground text-xs italic shrink-0">vacío</span>
    return <span className="text-xs font-medium text-foreground break-all min-w-0">{label}</span>
}

function ChangeRow({ entry }: { entry: AuditLogEntry }) {
    const fieldLabel = FIELD_LABELS[entry.field] ?? entry.field
    return (
        <div className="text-xs space-y-0.5">
            <span className="text-muted-foreground">{fieldLabel}:</span>
            <div className="flex items-start gap-1.5 flex-wrap min-w-0">
                <ValueChip field={entry.field} raw={entry.previousValue} />
                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                <ValueChip field={entry.field} raw={entry.newValue} />
            </div>
        </div>
    )
}

interface Props {
    instrumentId: string
}

export function AuditLogCard({ instrumentId }: Props) {
    const [showAll, setShowAll] = useState(false)

    const { data: entries = [], isLoading } = useQuery({
        queryKey: ["audit-log", instrumentId],
        queryFn: () => getInstrumentAuditLog(instrumentId),
    })

    if (isLoading) return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Cargando historial...
        </div>
    )

    if (entries.length === 0) return null

    const groups = groupEntries(entries)
    const visible = showAll ? groups : groups.slice(0, INITIAL_VISIBLE)
    const remaining = groups.length - INITIAL_VISIBLE

    return (
        <Card className="border-gray-200">
            <CardHeader className="pb-3 pt-5 px-5">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                    <History className="h-4 w-4 text-muted-foreground" />
                    Historial de cambios
                    <span className="ml-auto text-[10px] font-normal text-muted-foreground">
                        {groups.length} edición{groups.length === 1 ? "" : "es"}
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
                <div className="relative">
                    {/* línea vertical */}
                    <div className="absolute left-3.5 top-0 bottom-0 w-px bg-border" />

                    <div className="space-y-4">
                        {visible.map((group) => (
                            <div key={group.key} className="flex gap-3">
                                {/* Avatar */}
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold ring-2 ring-background z-10">
                                    {initials(group.userName)}
                                </div>

                                <div className="flex-1 min-w-0 pt-0.5">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span className="text-xs font-semibold text-foreground truncate">
                                            {group.userName}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground shrink-0">
                                            {formatRelative(group.changedAt)}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 space-y-1">
                                        {group.items.map(entry => (
                                            <ChangeRow key={entry.$id} entry={entry} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {groups.length > INITIAL_VISIBLE && (
                    <button
                        type="button"
                        onClick={() => setShowAll(prev => !prev)}
                        className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                        {showAll ? "Mostrar menos" : `Ver ${remaining} edición${remaining === 1 ? "" : "es"} más`}
                    </button>
                )}
            </CardContent>
        </Card>
    )
}
